/**
 * POST `admin/ai/simulate-inbound` — прогнать сообщение «пациента» через
 * боевой конвейер ассистента и получить ответ сразу. Нужны привязанный
 * номер WhatsApp и включённый режим ассистента. В режиме «Наблюдает»
 * ответ виден здесь, но пациенту бы не ушёл.
 */

import {apiEnabled, http} from '@services/utils';

export type SimulateInboundInput = {clinicId: string; fromPhone: string; text: string};

export type SimulatedMessage = {role: string; body: string; sent: boolean};

export type SimulateInboundResult = {
  /** sent | shadow | draft | escalated | skipped | deferred */
  outcome: string;
  botReply: string | null;
  escalationReason: string | null;
  /** Хвост диалога, новые снизу */
  messages: SimulatedMessage[];
};

type Raw = Record<string, unknown>;
const asObj = (v: unknown): Raw => (typeof v === 'object' && v !== null ? (v as Raw) : {});
const strN = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Мок: переписка по номеру копится, как на сервере — отдаём хвост */
const MOCK_THREADS = new Map<string, SimulatedMessage[]>();
const MOCK_TAIL = 20;

export const simulateInbound = async (
  input: SimulateInboundInput,
): Promise<SimulateInboundResult> => {
  if (!apiEnabled) {
    await sleep(900);
    const reply = 'Здравствуйте! Консультация стоит 500 сом. Записать вас на завтра?';
    const key = `${input.clinicId}:${input.fromPhone.replace(/\D/g, '')}`;
    const thread = [
      ...(MOCK_THREADS.get(key) ?? []),
      {role: 'patient', body: input.text.trim(), sent: true},
      {role: 'bot', body: reply, sent: false},
    ].slice(-MOCK_TAIL);
    MOCK_THREADS.set(key, thread);
    return {outcome: 'shadow', botReply: reply, escalationReason: null, messages: thread};
  }
  const r = asObj(
    await http('admin/ai/simulate-inbound', {
      method: 'POST',
      body: {clinicId: input.clinicId, fromPhone: input.fromPhone.trim(), text: input.text.trim()},
      skipCaseConversion: true,
    }),
  );
  return {
    outcome: strN(r.outcome) ?? 'skipped',
    botReply: strN(r.botReply),
    escalationReason: strN(r.escalationReason),
    messages: (Array.isArray(r.messages) ? r.messages : []).map((m) => {
      const x = asObj(m);
      return {role: strN(x.role) ?? 'bot', body: strN(x.body) ?? '', sent: x.sent === true};
    }),
  };
};
